import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { claimAllLpRewardsInteraction, claimLpRewardInteraction } from "@/domains/interactions"
import { SCALE } from "@/domains/tokens/static-tokens"
import { UniV2RewardsView } from "@/features/rewards/components/uni-v2-rewards-view"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

export const FarmingRewards = observer(() => {
  const account = useAccount()
  const chainConfig = useChainConfig()
  const config = useConfig()
  const emitter = useEventEmitter()
  const scaleApp = useScaleApplication()

  const poolsWithRewards = scaleApp.MinimalPools.Repository.getAllWithGaugeRewards()

  const onClaimUniV2LpReward = async (pool: (typeof poolsWithRewards)[0]) => {
    try {
      if (!account.address) {
        throw new Error("no address found")
      }
      if (!pool.hasGauge || !pool.gaugeAddress) {
        throw new Error("no gauge found for reward")
      }

      // Instead of joining the addresses, pass them as a string that's already properly formatted
      const governanceTokenHex = SCALE.address.toViemAddress()

      // Create a properly formatted string with both addresses
      const tokenAddressParam = governanceTokenHex

      await claimLpRewardInteraction({
        account: account.address,
        chainId: chainConfig.chainId,
        config,
        gaugeAddress: pool.gaugeAddress.toViemAddress() as `0x${string}`,
        tokenAddress: tokenAddressParam as `0x${string}`,
        pairDisplayName: pool.displayName ?? "unknown",
        emitter,
      })
      // MobX will automatically update UI when store data changes - no manual refetch needed
    } catch (error) {
      console.error("FarmingRewards→onClaimLpReward error", error)
    }
  }

  const onClaimAllLpRewards = async (pools: typeof poolsWithRewards) => {
    try {
      if (!account.address) {
        throw new Error("no address found")
      }
      await claimAllLpRewardsInteraction({
        account: account.address,
        chainId: chainConfig.chainId,
        config,
        voterAddress: chainConfig.equalizer.contracts.voter,
        governanceToken: SCALE as any, // SCALE is TokenStore, but claimAllLpRewardsInteraction expects Token type
        pairs: pools,
        rewardsForTable: pools,
        emitter,
        // MobX will automatically update UI when store data changes - no manual refetch needed
      })
    } catch (error) {
      console.error("FarmingRewards→onClaimAllLpRewards error", error)
    }
  }

  return (
    <UniV2RewardsView
      governanceToken={SCALE}
      pools={poolsWithRewards}
      onClaimAllLpRewards={onClaimAllLpRewards}
      onClaimUniV2LpReward={onClaimUniV2LpReward}
    />
  )
})
